import { type ComponentType, lazy, Suspense } from "react";
import {
  createBrowserRouter,
  Navigate,
  type RouteObject,
} from "react-router-dom";

import GlobalLayout from "@/apps/admin/components/GlobalLayout/GlobalLayout";
import { ADMIN_NOT_FOUND_SX } from "@/apps/admin/components/Layout/notFoundSx";
import {
  ADMIN_ROUTE_ERROR,
  lazyPage,
  PageLoading,
} from "@/apps/admin/components/LazyPage/LazyPage";
import {
  type CrumbLabelProps,
  withCrumbLabel,
} from "@/apps/admin/components/PageHeader/useRouteTrail";
import Authenticated from "@/apps/admin/features/authentication/Authenticated/Authenticated";
import { GenieKnowledgeGate } from "@/apps/admin/features/authentication/GenieKnowledgeGate/GenieKnowledgeGate";
import PermissionService from "@/apps/admin/features/authentication/Permission/PermissionService";
import { isHiddenForMvp } from "@/apps/admin/pages/pipeline/mvpScope";
import {
  collectorsSearchTrail,
  runsSearchTrail,
} from "@/apps/admin/pages/pipeline/searchTrail";
import NotFound from "@/common/components/NotFound/NotFound";

import { paths } from "./paths";

// The route table lives in ./paths, which pages, TopNav and PageHeader import, so none of them
// imports the router (and every page's loader). Only GlobalLayout still reads it from here, a
// router ↔ GlobalLayout import cycle; the re-export goes when GlobalLayout (P6's) imports ./paths.
export { paths };

// The Pipeline section's layouts and crumbs load with the section, not with the admin shell: a
// static import here put the pipeline API and its words in every admin page's first download
// (the bundle budget, scripts/ci/bundle-budgets.json).
const lazyLayout = (load: () => Promise<ComponentType>) => {
  const Layout = lazy(() =>
    load().then((component) => ({ default: component }))
  );
  return (
    <Suspense fallback={<PageLoading />}>
      <Layout />
    </Suspense>
  );
};

/** A crumb label loaded on first use; until then the crumb shows the route's title. */
const lazyCrumb = (
  load: () => Promise<ComponentType<CrumbLabelProps>>
): ComponentType<CrumbLabelProps> => {
  const Label = lazy(() =>
    load().then((component) => ({ default: component }))
  );
  const LazyCrumb = (props: CrumbLabelProps) => (
    <Suspense fallback={props.title}>
      <Label {...props} />
    </Suspense>
  );
  return LazyCrumb;
};

/** Old pipeline links redirect from a small chunk of their own (it reads the section's paths). */
const LazyLegacyRedirect = lazy(() =>
  import("@/apps/admin/pages/pipeline/legacyRedirects").then((m) => ({
    default: m.LegacyRedirect,
  }))
);
const LegacyRedirect = () => (
  <Suspense fallback={<PageLoading />}>
    <LazyLegacyRedirect />
  </Suspense>
);

const loadEntityCrumbs = () =>
  import("@/apps/admin/pages/pipeline/components/EntityCrumbs");
const RunCrumb = lazyCrumb(() => loadEntityCrumbs().then((m) => m.RunCrumb));
const TaskCrumb = lazyCrumb(() => loadEntityCrumbs().then((m) => m.TaskCrumb));
const TermSetCrumb = lazyCrumb(() =>
  loadEntityCrumbs().then((m) => m.TermSetCrumb)
);

/** The admin route table, apart from the router so a test can render it in memory. */
export const adminRoutes: RouteObject[] = [
  {
    path: paths.ROOT.path,
    element: (
      <Authenticated>
        <GlobalLayout />
        <PermissionService />
      </Authenticated>
    ),
    errorElement: ADMIN_ROUTE_ERROR,
    children: [
      {
        index: true,
        ...lazyPage(() => import("@/apps/admin/pages/LandingPage")),
      },
      {
        path: paths.ACCESS_CONTROL.path,
        handle: paths.ACCESS_CONTROL,
        children: [
          {
            index: true,
            ...lazyPage(() => import("@/apps/admin/pages/AccessControl")),
          },
        ],
      },
      {
        path: paths.ORGANIZATIONS.path,
        handle: paths.ORGANIZATIONS,
        children: [
          {
            index: true,
            ...lazyPage(() => import("@/apps/admin/pages/Organizations")),
          },
        ],
      },
      {
        path: paths.GLOBAL_CONTENTS.path,
        handle: paths.GLOBAL_CONTENTS,
        children: [
          {
            path: paths.GLOBAL_CONTENT.path,
            ...lazyPage(
              () => import("@/apps/admin/pages/GlobalContentSummary")
            ),
            handle: paths.GLOBAL_CONTENT,
          },
        ],
      },
      {
        path: paths.STAKEHOLDERS.path,
        handle: paths.STAKEHOLDERS,
        children: [
          {
            index: true,
            ...lazyPage(() => import("@/apps/admin/pages/Stakeholders")),
          },
          {
            path: paths.STAKEHOLDER_DETAILS.path,
            ...lazyPage(() => import("@/apps/admin/pages/StakeholderDetails")),
            handle: paths.STAKEHOLDER_DETAILS,
          },
        ],
      },
      {
        path: paths.SUPERPROMPTS.path,
        handle: paths.SUPERPROMPTS,
        children: [
          {
            index: true,
            ...lazyPage(() => import("@/apps/admin/pages/Superprompts")),
          },
        ],
      },
      {
        path: paths.SCRAPERS.path,
        handle: paths.SCRAPERS,
        children: [
          {
            index: true,
            ...lazyPage(() => import("@/apps/admin/pages/Scrapers")),
          },
          {
            path: paths.SCRAPER_DETAILS.path,
            ...lazyPage(() => import("@/apps/admin/pages/ScraperDetails")),
            handle: paths.SCRAPER_DETAILS,
          },
        ],
      },
      {
        path: paths.INSIGHT_TOOLS.path,
        handle: paths.INSIGHT_TOOLS,
        children: [
          {
            index: true,
            ...lazyPage(
              () => import("@/apps/admin/pages/InsightTools/InsightToolsPage")
            ),
          },
        ],
      },
      {
        path: paths.SPECIAL_PERMISSIONS.path,
        handle: paths.SPECIAL_PERMISSIONS,
        children: [
          {
            index: true,
            ...lazyPage(() => import("@/apps/admin/pages/SpecialPermissions")),
          },
        ],
      },
      {
        path: paths.MANAGE_ASSETS.path,
        handle: paths.MANAGE_ASSETS,
        children: [
          {
            index: true,
            ...lazyPage(
              () => import("@/apps/admin/pages/ManageAssets/ManageAssetsPage")
            ),
          },
        ],
      },
      {
        path: paths.GENIE_KNOWLEDGE.path,
        handle: paths.GENIE_KNOWLEDGE,
        // TSC admin management only (global management.manage); anyone else sees not-found.
        element: <GenieKnowledgeGate />,
        children: [
          {
            index: true,
            ...lazyPage(
              () =>
                import("@/apps/admin/pages/GenieKnowledge/GenieKnowledgePage")
            ),
          },
        ],
      },
      {
        path: paths.GENIE_PLUS_USAGE.path,
        handle: paths.GENIE_PLUS_USAGE,
        children: [
          {
            index: true,
            ...lazyPage(
              () =>
                import("@/apps/admin/pages/GeniePlusUsage/GeniePlusUsagePage")
            ),
          },
        ],
      },
      {
        path: paths.PIPELINE.path,
        handle: paths.PIPELINE,
        // Global management.read; anyone else sees the section's own "access required" page.
        element: lazyLayout(() =>
          import("@/apps/admin/pages/pipeline/PipelineGate").then(
            (m) => m.PipelineGate
          )
        ),
        children: [
          {
            // D66: five tabs (Today, Collectors, Runs, Providers, Usage), over every page of the
            // section, detail pages too (FB-7 #1).
            element: lazyLayout(() =>
              import("@/apps/admin/pages/pipeline/components/PipelineSectionNav").then(
                (m) => m.PipelineSectionLayout
              )
            ),
            children: [
              {
                index: true,
                element: <Navigate to={paths.PIPELINE_TODAY.path} replace />,
              },
              {
                path: paths.PIPELINE_TODAY.path,
                handle: paths.PIPELINE_TODAY,
                ...lazyPage(
                  () => import("@/apps/admin/pages/pipeline/today/TodayPage")
                ),
              },
              {
                path: paths.PIPELINE_COLLECTORS.path,
                handle: {
                  ...paths.PIPELINE_COLLECTORS,
                  searchTrail: collectorsSearchTrail,
                },
                children: [
                  {
                    // UX-3 P1: the explorer | the chosen item (`?item=&run=&edit=1`, `?view=`).
                    index: true,
                    ...lazyPage(
                      () =>
                        import("@/apps/admin/pages/pipeline/collectors/CollectorsPage")
                    ),
                  },
                  // MVP-TRIM: Term sets are hidden; an old address goes to the Collectors list.
                  ...(isHiddenForMvp("termSets")
                    ? [
                        {
                          path: `${paths.PIPELINE_TERM_SETS.path}/*`,
                          element: <LegacyRedirect />,
                        },
                      ]
                    : [
                        {
                          path: paths.PIPELINE_TERM_SETS.path,
                          handle: paths.PIPELINE_TERM_SETS,
                          children: [
                            // The list is the explorer's Term sets view.
                            { index: true, element: <LegacyRedirect /> },
                            {
                              path: paths.PIPELINE_TERM_SET_NEW.path,
                              handle: paths.PIPELINE_TERM_SET_NEW,
                              ...lazyPage(
                                () =>
                                  import("@/apps/admin/pages/pipeline/termSets/CreateTermSetPage")
                              ),
                            },
                            {
                              path: paths.PIPELINE_TERM_SET.path,
                              handle: withCrumbLabel(
                                paths.PIPELINE_TERM_SET,
                                TermSetCrumb
                              ),
                              ...lazyPage(
                                () =>
                                  import("@/apps/admin/pages/pipeline/termSets/TermSetDetailPage")
                              ),
                            },
                          ],
                        },
                      ]),
                  // Before UX-3: the Workspaces view, the New page and a collector's own page,
                  // each forwarded to its place in the explorer.
                  ...[
                    paths.PIPELINE_LEGACY_WORKSPACES,
                    paths.PIPELINE_LEGACY_COLLECTOR_NEW,
                    paths.PIPELINE_LEGACY_COLLECTOR,
                    paths.PIPELINE_LEGACY_COLLECTORS_LIVE,
                    paths.PIPELINE_LEGACY_SANDBOX_RUNS,
                    paths.PIPELINE_LEGACY_TEMPLATES,
                  ].map(({ path }) => ({ path, element: <LegacyRedirect /> })),
                ],
              },
              {
                // UX-3 P5: every run beside the open one; the skipped items, the workers, and a
                // run's tasks under it. `/runs/:runId` forwards to `/runs?run=`.
                path: paths.PIPELINE_RUNS.path,
                handle: {
                  ...paths.PIPELINE_RUNS,
                  searchTrail: runsSearchTrail,
                },
                children: [
                  {
                    index: true,
                    ...lazyPage(
                      () => import("@/apps/admin/pages/pipeline/runs/RunsPage")
                    ),
                  },
                  // MVP-TRIM: the Skipped lists are hidden; an old address goes to the Collectors list.
                  isHiddenForMvp("skippedList")
                    ? {
                        path: paths.PIPELINE_RUNS_SKIPPED.path,
                        element: <LegacyRedirect />,
                      }
                    : {
                        path: paths.PIPELINE_RUNS_SKIPPED.path,
                        handle: paths.PIPELINE_RUNS_SKIPPED,
                        ...lazyPage(
                          () =>
                            import("@/apps/admin/pages/pipeline/skipped/SkippedPage")
                        ),
                      },
                  {
                    path: paths.PIPELINE_RUNS_WORKERS.path,
                    handle: paths.PIPELINE_RUNS_WORKERS,
                    ...lazyPage(
                      () =>
                        import("@/apps/admin/pages/pipeline/workers/WorkersPage")
                    ),
                  },
                  {
                    path: paths.PIPELINE_RUN.path,
                    handle: withCrumbLabel(paths.PIPELINE_RUN, RunCrumb),
                    children: [
                      {
                        index: true,
                        ...lazyPage(
                          () =>
                            import("@/apps/admin/pages/pipeline/runs/RunDetailPage")
                        ),
                      },
                      // D56: what the run skipped, by reason and step (hidden for the MVP).
                      isHiddenForMvp("skippedList")
                        ? {
                            path: paths.PIPELINE_RUN_SKIPPED.path,
                            element: <LegacyRedirect />,
                          }
                        : {
                            path: paths.PIPELINE_RUN_SKIPPED.path,
                            handle: paths.PIPELINE_RUN_SKIPPED,
                            ...lazyPage(
                              () =>
                                import("@/apps/admin/pages/pipeline/skipped/RunSkippedPage")
                            ),
                          },
                      {
                        // D40 §5: a task is a page under its run.
                        path: paths.PIPELINE_TASK.path,
                        handle: withCrumbLabel(paths.PIPELINE_TASK, TaskCrumb),
                        ...lazyPage(
                          () =>
                            import("@/apps/admin/pages/pipeline/tasks/TaskPage")
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                // UX-3 P6: one row per page across every collector and run (`?q=&state=`).
                path: paths.PIPELINE_OUTPUTS.path,
                handle: paths.PIPELINE_OUTPUTS,
                ...lazyPage(
                  () =>
                    import("@/apps/admin/pages/pipeline/outputs/OutputsPage")
                ),
              },
              {
                path: paths.PIPELINE_PROVIDERS.path,
                handle: paths.PIPELINE_PROVIDERS,
                // FB-41 #1: Catalog and Your providers, each its own route.
                element: lazyLayout(() =>
                  import("@/apps/admin/pages/pipeline/providers/ProvidersLayout").then(
                    (m) => m.ProvidersLayout
                  )
                ),
                children: [
                  {
                    index: true,
                    handle: paths.PIPELINE_PROVIDERS_DIRECTORY,
                    ...lazyPage(
                      () =>
                        import("@/apps/admin/pages/pipeline/providers/YourProvidersPage")
                    ),
                  },
                  {
                    path: paths.PIPELINE_PROVIDERS_CATALOG.path,
                    handle: paths.PIPELINE_PROVIDERS_CATALOG,
                    ...lazyPage(
                      () =>
                        import("@/apps/admin/pages/pipeline/providers/CatalogPage")
                    ),
                  },
                  {
                    path: paths.PIPELINE_LEGACY_PROVIDERS_ADD.path,
                    element: <LegacyRedirect />,
                  },
                  {
                    path: paths.PIPELINE_LEGACY_PROVIDERS_HEALTH.path,
                    element: <LegacyRedirect />,
                  },
                ],
              },
              // D66: usage, limits and spend (hidden for the MVP: the address goes to Collectors).
              isHiddenForMvp("usage")
                ? {
                    path: paths.PIPELINE_USAGE.path,
                    element: <LegacyRedirect />,
                  }
                : {
                    path: paths.PIPELINE_USAGE.path,
                    handle: paths.PIPELINE_USAGE,
                    ...lazyPage(
                      () =>
                        import("@/apps/admin/pages/pipeline/providers/HealthPage")
                    ),
                  },
              ...[
                paths.PIPELINE_LEGACY_OVERVIEW,
                paths.PIPELINE_LEGACY_OPERATIONS,
                paths.PIPELINE_LEGACY_WORKERS,
                paths.PIPELINE_LEGACY_SEARCH,
                paths.PIPELINE_LEGACY_SOURCES,
                paths.PIPELINE_LEGACY_TERM_SETS,
                paths.PIPELINE_LEGACY_MONITORS,
              ].map(({ path }) => ({ path, element: <LegacyRedirect /> })),
            ],
          },
        ],
      },
      {
        path: "*",
        handle: { title: "Page not found" },
        element: <NotFound sx={ADMIN_NOT_FOUND_SX} />,
      },
    ],
  },
];

export const router = createBrowserRouter(adminRoutes);
